import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/index.css";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Fleet from "@/pages/Fleet";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Reservation from "@/pages/Reservation";
import Contact from "@/pages/Contact";
import CorporateDemo from "@/pages/CorporateDemo";
import Legal from "@/pages/Legal";
import Admin from "@/pages/Admin";
import AuthCallback from "@/components/AuthCallback";
import { ClientAuthProvider } from "@/lib/clientAuth";
import ClientLogin from "@/pages/portal/ClientLogin";
import ClientRegister from "@/pages/portal/ClientRegister";
import { VerifyEmail, ForgotPassword, ResetPassword, AcceptInvite, ClientAuthCallback } from "@/pages/portal/ClientAux";
import Dashboard from "@/pages/portal/Dashboard";
import PortalPreview from "@/pages/PortalPreview";
import Operations from "@/pages/ops/Operations";
import DriverPortal from "@/pages/driver/DriverPortal";
import DriverAccept from "@/pages/driver/DriverAccept";
import { initLenis, scrollToTop } from "@/lib/smooth";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-obsidian-900 px-6 text-center">
          <div>
            <p className="font-display text-3xl text-ivory">Something went off-route.</p>
            <p className="mt-3 text-sm text-ivory/60">Please refresh the page.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    scrollToTop();
  }, [pathname]);
  return null;
}

function Shell() {
  const location = useLocation();
  useEffect(() => {
    initLenis();
  }, []);

  // Emergent ADMIN auth returns to /admin#session_id=... — handle before routing.
  if (location.hash?.includes("session_id=") && location.pathname.startsWith("/admin")) {
    return <AuthCallback />;
  }

  const portalPaths = ["/login", "/corporate-login", "/corporate/login", "/register", "/verify-email",
    "/forgot-password", "/reset-password", "/accept-invite", "/client-auth", "/portal"];
  const isAdmin = location.pathname.startsWith("/admin");
  const isPortal = portalPaths.some((p) => location.pathname.startsWith(p));
  const hideChrome = isAdmin || isPortal || location.pathname.startsWith("/corporate-demo") || location.pathname.startsWith("/portal-preview") || location.pathname.startsWith("/operations") || location.pathname.startsWith("/driver");

  return (
    <div className="grain min-h-screen bg-obsidian-900 text-ivory">
      {!hideChrome && <Nav />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        <Route path="/reservation" element={<Reservation />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<ClientLogin />} />
        <Route path="/corporate-login" element={<ClientLogin corporate />} />
        <Route path="/corporate/login" element={<ClientLogin corporate />} />
        <Route path="/register" element={<ClientRegister />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/client-auth" element={<ClientAuthCallback />} />
        <Route path="/portal" element={<Dashboard />} />
        <Route path="/portal-preview" element={<PortalPreview />} />
        <Route path="/operations" element={<Operations />} />
        <Route path="/driver" element={<DriverPortal />} />
        <Route path="/driver/accept" element={<DriverAccept />} />
        <Route path="/corporate-demo" element={<CorporateDemo />} />
        <Route path="/privacy" element={<Legal />} />
        <Route path="/terms" element={<Legal />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Home />} />
      </Routes>
      {!hideChrome && <Footer />}
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ClientAuthProvider>
          <ScrollToTop />
          <Shell />
        </ClientAuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
